import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { track } from "../lib/analytics";
import { useWaitlistStats } from "../lib/useWaitlistStats";

/**
 * Waitlist status page for logged-in users who did NOT make it into the first 250.
 * Now includes:
 *   (a) live member ticker + arrival toasts via WS /api/ws/waitlist
 *   (b) 5-tier unlock roadmap with current-tier highlight + progress meter
 *   (c) invite-to-jump-queue CTA (reuses /auth/my-invites)
 *   (d) blurred Season One preview grid (fetches /lives/season)
 */

const TIER_COLORS = {
  founding:       { bg: "#161616", ink: "#FFFFFF", accent: "#FF6A00", shadow: "#264733" },
  early_insiders: { bg: "#FFFFFF", ink: "#161616", accent: "#264733", shadow: "#264733" },
  pioneers:       { bg: "#FFFFFF", ink: "#161616", accent: "#8B5CF6", shadow: "#8B5CF6" },
  trailblazers:   { bg: "#FFFFFF", ink: "#161616", accent: "#0EA5E9", shadow: "#0EA5E9" },
  drop_nation:    { bg: "#FFFFFF", ink: "#161616", accent: "#F59E0B", shadow: "#F59E0B" },
};

export default function Waitlist() {
  const { user, logout } = useAuth();
  const stats = useWaitlistStats();
  const [tiers, setTiers] = useState(null);
  const [season, setSeason] = useState(null);
  const [arrivals, setArrivals] = useState([]); // rolling toast queue
  const lastCountRef = useRef(null);

  // --- Fetch static data (tiers ladder + season preview) once ---
  useEffect(() => {
    api.get("/waitlist/tiers").then((r) => setTiers(r.data)).catch(() => {});
    api.get("/lives/season").then((r) => setSeason(r.data)).catch(() => {});
    track("waitlist_view", { member_no: user?.member_no });
  }, [user?.member_no]);

  // --- Emit arrival toasts whenever the WS-fed total_signups increases ---
  useEffect(() => {
    const nextTotal = stats?.total_signups ?? null;
    if (nextTotal === null) return;
    const prevTotal = lastCountRef.current;
    if (prevTotal !== null && nextTotal > prevTotal) {
      const newMembers = [];
      for (let n = prevTotal + 1; n <= nextTotal; n += 1) newMembers.push(n);
      setArrivals((prev) => {
        const merged = [...prev, ...newMembers.map((no) => ({ no, id: `${no}-${Date.now()}` }))];
        return merged.slice(-3);
      });
      newMembers.forEach((no) => {
        setTimeout(() => {
          setArrivals((prev) => prev.filter((t) => t.no !== no));
        }, 3500);
      });
    }
    lastCountRef.current = nextTotal;
  }, [stats?.total_signups]);

  if (!user) {
    return (
      <div className="min-h-screen bg-[#F8F7F2] flex items-center justify-center p-6">
        <Link to="/" className="btn-primary" data-testid="waitlist-go-landing">← Back to landing</Link>
      </div>
    );
  }

  const myNo = user.member_no || 0;
  const padded = String(myNo).padStart(3, "0");
  const totalJoined = stats?.total_signups ?? 0;
  const foundingActive = stats?.founding_active ?? 0;
  const openSpots = stats?.spots_left ?? 0;
  const foundingFull = stats?.founding_full ?? (openSpots === 0);
  const myTierKey = user.tier || "early_insiders";

  return (
    <div className="min-h-screen bg-[#F8F7F2] text-[#161616]" data-testid="waitlist-page">
      {/* Header strip */}
      <div className="border-b border-[#161616] px-6 md:px-10 h-11 flex items-center justify-between font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#6E6E6E]">
        <span>
          <span className="text-[#264733] font-bold">Waitlist Status</span>
          <span className="opacity-40 mx-2">·</span>
          <span>{user.email}</span>
        </span>
        <button onClick={logout} className="hover:text-[#FF6A00]" data-testid="waitlist-signout">
          Sign out →
        </button>
      </div>

      {/* Arrival toast dock — top right */}
      <div className="fixed top-16 right-4 z-40 flex flex-col gap-2 pointer-events-none" data-testid="waitlist-arrivals">
        {arrivals.map((t) => (
          <div
            key={t.id}
            data-testid={`waitlist-arrival-${t.no}`}
            className="animate-arrival-in pointer-events-auto border-2 border-[#161616] bg-[#FFFFFF] shadow-[4px_4px_0_#264733] px-4 py-2.5 font-mono-ds text-[11px] uppercase tracking-[0.18em] flex items-center gap-2"
          >
            <span className="inline-block w-2 h-2 bg-[#264733] rounded-full animate-pulse" />
            <span>
              <span className="text-[#264733] font-bold">Member #{String(t.no).padStart(3, "0")}</span> just joined
            </span>
          </div>
        ))}
      </div>

      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-12 pb-24">
        {/* ---------- HERO: You are #NNN ---------- */}
        <div className="brand-tag mb-4">// Ledger Entry</div>
        <h1 className="font-display uppercase leading-[0.95] tracking-tight text-5xl sm:text-6xl md:text-7xl">
          You are <span className="text-outline">#{padded}.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-[#6E6E6E] text-base md:text-lg leading-relaxed">
          Drop Shop Live is in a private Founding Members phase (first 250 signups). Your number is locked in — we&rsquo;ll unlock the doors for your tier as we scale.
        </p>

        {/* Live ticker pill */}
        <div className="mt-6 inline-flex items-center gap-3 border-2 border-[#161616] bg-[#FFFFFF] shadow-[3px_3px_0_#161616] px-4 h-11 font-mono-ds text-[11px] uppercase tracking-[0.2em]" data-testid="waitlist-live-ticker">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#264733] opacity-60" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#264733]" />
          </span>
          <span>
            <span className="text-[#264733] font-bold tabular-nums" data-testid="waitlist-live-count">{foundingActive}</span>
            <span className="opacity-40 mx-2">·</span>
            <span>active founding</span>
            <span className="opacity-40 mx-2">·</span>
            <span className={openSpots > 0 ? "text-[#FF6A00] font-bold" : "text-[#161616] font-bold"} data-testid="waitlist-live-spots">
              {openSpots > 0 ? `${openSpots} spots left` : "Founding cohort full"}
            </span>
          </span>
        </div>

        {/* Grid: Left = badge + roadmap, Right = invites + season preview */}
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {/* --------- LEFT (2 cols) : Badge + Tier Roadmap --------- */}
          <div className="md:col-span-2 space-y-6">
            <MemberBadgeCard user={user} myNo={myNo} padded={padded} totalJoined={totalJoined} />
            <TierRoadmap tiers={tiers} totalJoined={totalJoined} myNo={myNo} myTierKey={myTierKey} />
            <SeasonPreview season={season} />
          </div>

          {/* --------- RIGHT (1 col) : Invite CTA + notify note --------- */}
          <div className="space-y-6">
            <JumpQueueInviteCard memberNo={myNo} foundingFull={foundingFull} />
            <NotifyBlock email={user.email} />
          </div>
        </div>
      </section>
    </div>
  );
}

/* --------- Member Badge Card --------- */
function MemberBadgeCard({ user, myNo, padded, totalJoined }) {
  const ahead = Math.max(0, myNo - 251);
  return (
    <div className="border-2 border-[#161616] bg-[#FFFFFF] shadow-[6px_6px_0_#264733] p-6 md:p-8" data-testid="waitlist-badge">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-5xl leading-none">{user.emoji || "🚀"}</div>
          <div className="font-display text-3xl md:text-4xl uppercase tracking-tight mt-3">
            {user.label || "Early Insiders"}
          </div>
          <div className="brand-tiny text-[#6E6E6E] mt-2">
            Member No. {padded}
            {ahead > 0 && (
              <>
                <span className="opacity-40 mx-2">·</span>
                <span>{ahead} ahead of you in this tier</span>
              </>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="brand-tiny text-[#6E6E6E]">Locked in on</div>
          <div className="font-mono-ds text-[13px] mt-1 uppercase tracking-widest">
            {user.created_at ? new Date(user.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
          </div>
        </div>
      </div>

      {/* Progress meter to next unlock */}
      {myNo > 250 && (
        <div className="mt-6 border-t border-[#E5E1D8] pt-5">
          <div className="flex items-baseline justify-between mb-2">
            <div className="brand-tiny text-[#6E6E6E]">Position in queue</div>
            <div className="font-mono-ds text-[11px] uppercase tracking-widest text-[#161616] font-bold tabular-nums">
              #{padded} / {totalJoined || myNo}
            </div>
          </div>
          <div className="h-2 bg-[#F8F7F2] border border-[#161616] relative overflow-hidden">
            <div
              className="h-full bg-[#FF6A00]"
              style={{ width: `${Math.min(100, (250 / Math.max(myNo, totalJoined || myNo)) * 100)}%` }}
            />
            <div
              className="absolute top-0 h-full w-0.5 bg-[#264733]"
              style={{ left: `${Math.min(100, (myNo / Math.max(myNo, totalJoined || myNo)) * 100)}%` }}
              title={`You · #${padded}`}
            />
          </div>
          <div className="mt-2 flex items-center justify-between font-mono-ds text-[10px] uppercase tracking-widest text-[#6E6E6E]">
            <span>◆ Founding cap 250</span>
            <span>You are #{padded}</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* --------- Tier Unlock Roadmap --------- */
function TierRoadmap({ tiers, totalJoined, myNo, myTierKey }) {
  const ladder = tiers?.tiers ?? [];
  const activeIndex = ladder.findIndex((t) => t.key === myTierKey);

  return (
    <div className="border-2 border-[#161616] bg-[#FFFFFF] shadow-[4px_4px_0_#161616] p-6" data-testid="waitlist-roadmap">
      <div className="brand-tag mb-2">// Tier Unlock Roadmap · 002</div>
      <div className="font-display text-2xl uppercase tracking-tight leading-tight">
        The <span className="text-outline">5 cohorts.</span>
      </div>
      <p className="mt-3 text-sm text-[#6E6E6E]">
        Founding Members unlock first. As they attend live drops we open the next cohort. You&rsquo;ll be notified the moment your tier goes live.
      </p>

      <ol className="mt-6 space-y-3">
        {ladder.map((t, i) => {
          const isMine = i === activeIndex;
          const isUnlocked = i === 0; // only founding is currently live
          const c = TIER_COLORS[t.key] || TIER_COLORS.early_insiders;
          const range = t.range_to ? `${t.range_from} – ${t.range_to}` : `${t.range_from}+`;
          return (
            <li
              key={t.key}
              data-testid={`tier-row-${t.key}`}
              className={`flex items-center gap-4 border-2 p-4 transition-all ${
                isMine
                  ? "border-[#161616] bg-[#161616] text-white shadow-[4px_4px_0_" + c.accent + "]"
                  : isUnlocked
                    ? "border-[#264733] bg-[#B9E641]/20"
                    : "border-[#E5E1D8] bg-[#FAF7F2]"
              }`}
            >
              <div className={`text-3xl leading-none ${isUnlocked || isMine ? "" : "grayscale opacity-60"}`}>{t.emoji}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className={`font-display text-lg md:text-xl uppercase tracking-tight ${isMine ? "text-white" : "text-[#161616]"}`}>
                    {t.label}
                  </div>
                  {isUnlocked && !isMine && (
                    <span className="font-mono-ds text-[9px] uppercase tracking-widest bg-[#264733] text-white px-1.5 py-0.5">
                      ● Unlocked
                    </span>
                  )}
                  {isMine && (
                    <span className="font-mono-ds text-[9px] uppercase tracking-widest bg-[#FF6A00] text-[#161616] px-1.5 py-0.5" data-testid={`tier-row-${t.key}-you`}>
                      ★ You are here
                    </span>
                  )}
                </div>
                <div className={`brand-tiny mt-1 ${isMine ? "text-white/70" : "text-[#6E6E6E]"}`}>
                  Member No. {range}
                </div>
              </div>
              <div className={`font-mono-ds text-[11px] uppercase tracking-widest ${isMine ? "text-white/60" : "text-[#6E6E6E]"} hidden sm:block`}>
                {isUnlocked ? "Live now" : isMine ? "Opens next" : "Queued"}
              </div>
            </li>
          );
        })}
      </ol>

      <div className="mt-5 border-t border-[#E5E1D8] pt-4 font-mono-ds text-[11px] uppercase tracking-widest text-[#6E6E6E]">
        <span className="text-[#161616]">◆</span> Total signups so far: <span className="text-[#161616] font-bold tabular-nums">{totalJoined || "—"}</span>
        <span className="opacity-40 mx-2">·</span>
        You are <span className="text-[#161616] font-bold">#{myNo}</span>
      </div>
    </div>
  );
}

/* --------- Season One Preview (blurred locked cards) --------- */
function SeasonPreview({ season }) {
  const slots = season?.slots ?? [];
  return (
    <div className="border-2 border-[#161616] bg-[#161616] text-white shadow-[6px_6px_0_#FF6A00] p-6 md:p-8" data-testid="waitlist-season-preview">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono-ds text-[11px] uppercase tracking-[0.24em] text-[#FF6A00]">// Season One · 003</div>
          <div className="font-display text-2xl md:text-3xl uppercase tracking-tight leading-tight mt-2">
            15 drops. <span className="text-[#B9E641]">Locked</span> for now.
          </div>
        </div>
        <div className="font-mono-ds text-[10px] uppercase tracking-widest text-white/50 max-w-[220px] text-right hidden md:block">
          Preview of the drops Founding Members will unlock first
        </div>
      </div>

      <div className="mt-6 grid grid-cols-3 sm:grid-cols-5 gap-2.5">
        {(slots.length ? slots : Array.from({ length: 15 }, (_, i) => ({ drop_no: i + 1 }))).map((slot) => {
          const dropNum = String(slot.drop_no || 1).padStart(3, "0");
          const category = slot.product_category || slot.category || "—";
          const dateStr = slot.date_ist ? new Date(slot.date_ist).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "";
          return (
            <div
              key={slot.drop_no}
              data-testid={`season-preview-${dropNum}`}
              className="relative aspect-square border border-white/15 bg-white/[0.04] p-2.5 flex flex-col justify-between overflow-hidden group hover:border-[#FF6A00]/60 transition-colors"
            >
              {/* blurred content layer */}
              <div className="absolute inset-0 blur-[3px] opacity-60">
                <div className="absolute inset-0 bg-gradient-to-br from-[#FF6A00]/10 via-transparent to-[#B9E641]/10" />
              </div>
              {/* content */}
              <div className="relative z-10 flex flex-col justify-between h-full">
                <div className="font-mono-ds text-[9px] uppercase tracking-widest text-white/60">
                  #{dropNum}
                </div>
                <div className="flex items-center justify-center flex-1">
                  <span className="text-2xl opacity-70">🔒</span>
                </div>
                <div>
                  <div className="font-display text-[11px] uppercase tracking-tight leading-tight text-white truncate">
                    {category}
                  </div>
                  <div className="font-mono-ds text-[8.5px] uppercase tracking-widest text-white/50">
                    {dateStr}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-5 pt-4 border-t border-white/10 font-mono-ds text-[10px] uppercase tracking-widest text-white/50">
        ◇ Product reveals unlock the night of each drop · 21:00 IST · 2 minutes only
      </div>
    </div>
  );
}

/* --------- Invite-to-Jump-Queue CTA --------- */
function JumpQueueInviteCard({ memberNo, foundingFull }) {
  const [copied, setCopied] = useState(false);
  const [summary, setSummary] = useState(null);
  const code = `DSL${String(memberNo || 1).padStart(3, "0")}`;
  const shareLink = `${typeof window !== "undefined" ? window.location.origin : "https://dropshop.live"}/?invite=${code}`;

  useEffect(() => {
    api.get("/auth/my-invites").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  const invitesTotal = summary?.invites_total ?? 3;
  const invitesUsed = summary?.invites_used ?? 0;
  const remaining = summary?.invites_remaining ?? (invitesTotal - invitesUsed);
  const capped = remaining <= 0;

  const copy = () => {
    if (capped) return;
    navigator.clipboard?.writeText(shareLink);
    setCopied(true);
    track("waitlist_invite_copy", { member_no: memberNo, remaining });
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="border-2 border-[#161616] bg-[#FFFFFF] shadow-[4px_4px_0_#161616] p-6" data-testid="waitlist-invite">
      <div className="brand-tag mb-2">// Skip the queue · 004</div>
      <div className="font-display text-2xl uppercase tracking-tight leading-tight">
        Invite <span className="text-outline">3 friends.</span>
      </div>
      <p className="mt-3 text-sm text-[#6E6E6E]">
        {capped ? (
          <>You&rsquo;ve used all <span className="text-[#161616] font-bold">3 invite codes.</span> Thank you for spreading the drop.</>
        ) : foundingFull ? (
          <>Every friend who joins with your code strengthens your position when we open the next cohort. Founding is closed — but your tier opens sooner as we scale.</>
        ) : (
          <>Founding is still open. Every friend who joins with your code takes a Founding spot too — and your position <span className="text-[#161616] font-bold">locks in earlier</span>.</>
        )}
      </p>

      <div className="mt-5 flex items-baseline justify-between">
        <div>
          <div className="brand-tiny text-[#6E6E6E]">Invites left</div>
          <div className="font-display text-4xl tabular-nums leading-none mt-1" data-testid="waitlist-invite-remaining">
            {remaining}<span className="text-[#6E6E6E] text-2xl"> / {invitesTotal}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="brand-tiny text-[#6E6E6E]">Your code</div>
          <div className="font-mono-ds text-lg font-bold text-[#264733]" data-testid="waitlist-invite-code">{code}</div>
        </div>
      </div>

      <button
        onClick={copy}
        disabled={capped}
        className={`mt-5 h-11 px-4 w-full ${capped ? "btn-outline-ns opacity-60 cursor-not-allowed" : "btn-urgent"}`}
        data-testid="waitlist-invite-copy"
      >
        {capped ? "All invites used" : (copied ? "Link copied ✓" : "Copy invite link →")}
      </button>

      <div className="mt-4 pt-4 border-t border-[#E5E1D8] font-mono-ds text-[10px] uppercase tracking-widest text-[#6E6E6E]">
        Share via WhatsApp, iMessage, Instagram DMs — anywhere.
      </div>
    </div>
  );
}

/* --------- Notify block --------- */
function NotifyBlock({ email }) {
  return (
    <div className="border-2 border-dashed border-[#161616] bg-[#B9E641]/15 p-5" data-testid="waitlist-notify">
      <div className="brand-tag mb-2">// You&rsquo;ll hear from us</div>
      <p className="text-sm text-[#161616] leading-relaxed">
        The moment your tier unlocks, we email <span className="font-bold">{email}</span>. No spam. One nudge.
      </p>
    </div>
  );
}
